import { DeleteTwoTone } from '@ant-design/icons'
import { Button, Checkbox, Col, Form, Row, Typography } from 'antd'
import { CustomFieldClassType } from '@cims-online/shared'
import { HelpMessage } from 'utils/constants'
import CIMSFormItem from 'components/CIMSFormItem'
import CIMSSelect from 'components/CIMSSelect'
import UserRoleSelect from 'components/CIMSSelect/common/UserRoleSelect'
import { useUserRoleSelectContext } from 'components/CIMSSelect/common/UserRoleSelect/UserRoleSelectContext'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { CustomFieldToClassTypeFormItemsProps } from './types'

const CustomFieldToClassTypeFormItems: React.FC<
  CustomFieldToClassTypeFormItemsProps
> = ({ customFieldToClassTypes }) => {
  const cf2ctOptions = useOptions({
    items: CustomFieldClassType.values,
    value: 'id',
    label: 'type',
    sort: 'type',
  })
  const { toLabeledValues } = useUserRoleSelectContext()
  return (
    <>
      <Typography.Paragraph>
        Enable and configure this Custom Field for one or more supported record
        types. These types are where the Custom Field values will be configured
        for each record.
      </Typography.Paragraph>
      <Form.List
        name="customFieldToClassTypes"
        initialValue={customFieldToClassTypes?.map(
          ({ classTypeId, roles, users }) => ({
            classTypeId,
            editors: toLabeledValues({ roles, users }),
          })
        )}
      >
        {(fields, { add, remove }) => (
          <>
            <Row style={{ marginBottom: '1rem' }}>
              <Button type="primary" onClick={() => add()}>
                Add Record Type
              </Button>
            </Row>
            {fields.map(({ name, key }) => {
              const initialValues = customFieldToClassTypes?.[key]
              const edit = !!initialValues
              return (
                <Row key={key} gutter={8}>
                  {/* classTypeId, alwaysEditable, prominent, required, roleIds, userIds */}
                  <Col span={edit ? 10 : 6}>
                    <CIMSFormItem
                      name={[name, 'classTypeId']}
                      label="Type"
                      requiredMessage="Please select an associated record type"
                      disabled={{
                        disabled: edit,
                        reason: {
                          why: 'disabled',
                          message:
                            'Cannot change the record type of an existing Custom Field',
                        },
                      }}
                      help={HelpMessage.Administration.CustomFields.ClassTypes()}
                    >
                      <CIMSSelect
                        disabled={edit}
                        options={cf2ctOptions}
                        simple
                      />
                    </CIMSFormItem>
                  </Col>
                  <Col span={edit ? 13 : 6}>
                    <CIMSFormItem
                      name={[name, 'editors']}
                      label="Editors"
                      help={HelpMessage.Administration.CustomFields.Editors}
                    >
                      <UserRoleSelect mode="multiple" />
                    </CIMSFormItem>
                  </Col>
                  {!edit && (
                    <>
                      <Col span={4}>
                        <CIMSFormItem
                          name={[name, 'alwaysEditable']}
                          label="Always Editable"
                          valuePropName="checked"
                          help={
                            HelpMessage.Administration.CustomFields
                              .AlwaysEditable
                          }
                        >
                          <Checkbox />
                        </CIMSFormItem>
                      </Col>
                      <Col span={3}>
                        <CIMSFormItem
                          name={[name, 'required']}
                          label="Required"
                          valuePropName="checked"
                          help={
                            HelpMessage.Administration.CustomFields.Required
                          }
                        >
                          <Checkbox />
                        </CIMSFormItem>
                      </Col>
                      <Col span={4}>
                        <CIMSFormItem
                          name={[name, 'prominent']}
                          label="Prominent"
                          valuePropName="checked"
                          help={
                            HelpMessage.Administration.CustomFields.Prominent
                          }
                        >
                          <Checkbox />
                        </CIMSFormItem>
                      </Col>
                    </>
                  )}
                  <Col span={1}>
                    <DeleteTwoTone
                      style={{ fontSize: '1.5em', marginTop: '0.25em' }}
                      onClick={() => remove(name)}
                    />
                  </Col>
                </Row>
              )
            })}
          </>
        )}
      </Form.List>
    </>
  )
}

export default CustomFieldToClassTypeFormItems
